import { forwardRef } from 'react';
import {
  Platform,
  TextInput as RNTextInput,
  TextInputProps as RNTextInputProps,
  View,
} from 'react-native';
import { FaIcon, Icon } from 'shared/components/icon';

export type InputProps = RNTextInputProps & {
  className?: string;
  dense?: boolean;
  unstyled?: boolean;
  /** Shown inside the field before the text, such as a magnifying glass on search fields. */
  icon?: FaIcon;
  iconClassName?: string;
};

export type TextInputRef = any;

function splitNativeInputClassNames(className: string) {
  const tokens = className.split(/\s+/).filter(Boolean);
  const inputTokens: string[] = [];
  const containerTokens: string[] = [];

  for (const token of tokens) {
    if (
      token.startsWith('text-') ||
      token.startsWith('font-') ||
      token.startsWith('placeholder:') ||
      token.startsWith('leading-') ||
      token.startsWith('tracking-') ||
      token === 'italic' ||
      token === 'underline' ||
      token === 'uppercase' ||
      token === 'lowercase' ||
      token === 'capitalize'
    ) {
      inputTokens.push(token);
      continue;
    }

    containerTokens.push(token);
  }

  return {
    containerClassName: containerTokens.join(' '),
    inputClassName: inputTokens.join(' '),
  };
}

const TextInput = forwardRef<any, InputProps>(
  ({ className = '', dense, unstyled = false, icon, iconClassName = '', style, ...props }, ref) => {
    const maxLength = props.maxLength ?? (props.multiline ? 2000 : 255);
    const isDisabled =
      props.editable === false ||
      props.accessibilityState?.disabled === true ||
      (props as RNTextInputProps & { disabled?: boolean }).disabled === true;
    const webAccessibilityProps = props.accessibilityLabel
      ? ({ 'aria-label': props.accessibilityLabel } as Record<string, string>)
      : {};
    const useNativeChrome = Platform.OS !== 'web' && !unstyled;
    const { containerClassName, inputClassName } = splitNativeInputClassNames(className);
    const resolvedClassName = [
      className,
      'text-brand-brown-dark font-regular placeholder:text-brand-brown-dark/30 w-full outline-none focus:ring-0',
      useNativeChrome || unstyled
        ? ''
        : [
            'border-brand-brown-dark/15 rounded border',
            isDisabled
              ? 'bg-brand-brown-dark/5 text-brand-brown-dark/50 placeholder:text-brand-brown-dark/25 cursor-not-allowed'
              : 'bg-white hover:border-brand-brown-dark/30',
          ].join(' '),
      useNativeChrome || unstyled
        ? ''
        : dense
          ? 'px-3 py-2 text-sm md:text-base'
          : 'px-4 py-3 text-lg',
      icon && !useNativeChrome && !unstyled ? (dense ? 'pl-9' : 'pl-11') : '',
    ]
      .filter(Boolean)
      .join(' ');
    const nativeStyle = useNativeChrome
      ? {
          fontSize: dense ? 15 : 18,
          lineHeight: dense ? 20 : 22,
          color: isDisabled ? 'rgba(48, 25, 17, 0.5)' : '#301911',
          padding: 0,
          margin: 0,
          minWidth: 0,
          backgroundColor: 'transparent',
        }
      : undefined;
    const nativeContainerStyle = useNativeChrome
      ? {
          borderColor: 'rgba(48, 25, 17, 0.15)',
          borderWidth: 1,
          borderRadius: dense ? 10 : 12,
          backgroundColor: isDisabled ? 'rgba(48, 25, 17, 0.05)' : '#ffffff',
          paddingHorizontal: dense ? 12 : 16,
          paddingVertical: dense ? 8 : 12,
          overflow: 'hidden' as const,
          justifyContent: props.multiline ? ('flex-start' as const) : ('center' as const),
          ...(Platform.OS === 'ios' ? { borderCurve: 'continuous' as const } : {}),
        }
      : undefined;

    // NativeWind adds hooks for `animate-*` classes, so a class change must remount the icon.
    const iconElement = icon ? (
      <Icon
        key={iconClassName}
        icon={icon}
        className={`text-brand-brown-dark/60 ${dense ? 'size-4' : 'size-5'} ${iconClassName}`}
      />
    ) : null;

    if (useNativeChrome) {
      return (
        <View
          className={[containerClassName, icon ? 'flex-row items-center gap-2' : '']
            .filter(Boolean)
            .join(' ')}
          style={nativeContainerStyle}
        >
          {iconElement}
          <RNTextInput
            ref={ref}
            className={[
              inputClassName,
              'text-brand-brown-dark font-regular placeholder:text-brand-brown-dark/30 w-full',
            ]
              .filter(Boolean)
              .join(' ')}
            style={[nativeStyle, icon ? { flex: 1, width: undefined } : null, style]}
            maxLength={maxLength}
            {...webAccessibilityProps}
            {...props}
          />
        </View>
      );
    }

    const input = (
      <RNTextInput
        ref={ref}
        className={resolvedClassName}
        style={style}
        maxLength={maxLength}
        {...webAccessibilityProps}
        {...props}
      />
    );

    if (!iconElement) return input;

    return (
      <View className={'justify-center'}>
        {input}
        <View
          pointerEvents={'none'}
          className={`absolute items-center justify-center ${dense ? 'left-3' : 'left-4'}`}
        >
          {iconElement}
        </View>
      </View>
    );
  },
);

TextInput.displayName = 'TextInput';

export { TextInput };
